export function isLastChar(index, input) {
  return index === input.length - 1;
}

export function onlyNumbers(input) {
  if (!input) return "";
  return input.replace(/\D/g, "") || "";
}

export function generateRandomNumber(length) {
  return Array(length)
    .fill(1)
    .reduce(
      (acc, number) => `${acc}${Math.random().toString().substr(2, number)}`,
      ""
    );
}

export function generateChecksum(base, weight) {
  const digits = onlyNumbers(base);

  const weights =
    typeof weight === "number"
      ? Array(digits.length)
          .fill(0)
          .map((_, i) => weight - i)
      : weight;

  return digits
    .split("")
    .reduce((acc, digit, i) => acc + parseInt(digit, 10) * weights[i], 0);
}

export const ACRONYMS = ["cia", "cnpj", "cpf", "ltda", "me", "rg"];

export const PREPOSITIONS = [
  "a",
  "com",
  "da",
  "das",
  "de",
  "do",
  "dos",
  "e",
  "em",
  "na",
  "nas",
  "no",
  "nos",
  "o",
  "por",
  "sem",
];

export function capitalize(
  value,
  { lowerCaseWords = PREPOSITIONS, upperCaseWords = ACRONYMS } = {}
) {
  return value
    .split(" ")
    .filter((word) => !!word)
    .map((word, index) => {
      const lowerCaseWord = word.toLocaleLowerCase();
      if (index > 0 && lowerCaseWords.indexOf(lowerCaseWord) !== -1)
        return lowerCaseWord;
      const upperCaseWord = word.toLocaleUpperCase();
      if (upperCaseWords.indexOf(upperCaseWord) !== -1) return upperCaseWord;
      return upperCaseWord.charAt(0) + lowerCaseWord.substr(1);
    })
    .join(" ");
}

export function formatReal(value, options = { precision: 2 }) {
  return value
    .toFixed(options.precision)
    .replace(".", ",")
    .replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1.");
}

export function parseReal(value) {
  return parseInt(value.replace(/\D/g, "") || "0", 10) / 100;
}

export function formatBrPhoneNumber(phone) {
  if (!phone) return "";
  const digits = onlyNumbers(phone);

  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(
      6,
      10
    )}`;
  }

  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(
      7,
      11
    )}`;
  }

  return phone;
}

export function convertToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(",")[1]);
    reader.onerror = (error) => reject(error);
  });
}

export function generateSafeDocumentName(prefix, file) {
  const safePrefix = (prefix || "document").replace(/[^a-zA-Z0-9_-]/g, "_");
  const timestamp = Date.now();
  const extFromName = file?.name?.includes(".")
    ? file.name.split(".").pop().toLowerCase()
    : "";
  const extFromType = file?.type?.split("/")?.[1] || "";
  const extension = extFromName || extFromType || "bin";

  return `${safePrefix}_${timestamp}.${extension}`;
}


export const PROMPT_CNH =
  'Extraia o texto desta imagem de CNH. Retorne os dados estruturados como JSON no seguinte formato: { "nome_completo": "", "registro_cnh": "", "data_nascimento": "", "validade": "", "cat_hab": "", "cpf": "", "rg":"", "numero_documento": "", "orgao_emissor": "", "uf": "", "nomePai": "", "nomeMae": "" }. Lembre-se que cat_hab deverá sempre e somente retornar uma string do conjunto a seguir [AB, AC, AD, AE, BC, BD, BE, CD, CE, DE] que é a categoria da habilitação em questão, além disso quando eu peço rg o que eu quero é apenas o "doc identidade" não precisa trazer org emissor e uf no retorno do campo "rg" também ignore selos, assinaturas digitais e textos genéricos. Não use blocos de código. Responda somente com o JSON puro, sem formatação Markdown. sem quebra de linha.';
export const PROMPT_RG =
  'Extraia o texto desta imagem de RG. Retorne os dados estruturados como JSON no seguinte formato: { "nome_completo": "", "rg": "", "orgao_emissor": "", "cpf": "","uf": "", "data_nascimento": "", "data_emissao": "", "naturalidade": "", "nome_mae": "", "nome_pai": "" }. Ignore selos, assinaturas digitais e textos genéricos. Não use blocos de código. Responda somente com o JSON puro, sem formatação Markdown. sem quebra de linha.';
export const PROMPT_RESIDENCIA =
  'Extraia o texto desta imagem de comprovante de residência. Retorne os dados estruturados como JSON no seguinte formato: { "nome_completo": "", "endereco": "", "bairro": "", "cidade": "", "uf": "", "cep": "", "data_emissao": "", "complemento": "" }. Ignore selos, assinaturas digitais e textos genéricos. Não use blocos de código. Responda somente com o JSON puro, sem formatação Markdown. sem quebra de linha.';
export const PROMPT_ANTT =
  'Extraia o texto desta imagem de ANTT. Retorne os dados estruturados como JSON no seguinte formato: { "numero_registro": "", "nome_completo": "", "cpf": "", "categoria": "", "data_emissao": "", "data_validade": "", "orgao_emissor": "" }. Ignore selos, assinaturas digitais e textos genéricos. Não use blocos de código. Responda somente com o JSON puro, sem formatação Markdown. sem quebra de linha.';
export const PROMPT_CNPJ =
  'Extraia o texto desta imagem de CNPJ. Retorne os dados estruturados como JSON no seguinte formato: { "razao_social": "", "nome_fantasia": "", "cnpj": "", "data_abertura": "", "endereco": "", "bairro": "", "cidade": "", "uf": "", "cep": "", "telefone": "", "email": "", "complemento": "" }. Ignore selos, assinaturas digitais e textos genéricos. Não use blocos de código. Responda somente com o JSON puro, sem formatação Markdown. sem quebra de linha.';
export const PROMPT_CRLV =
  'Extraia o texto desta imagem de CRLV. Retorne os dados estruturados como JSON no seguinte formato: { "placa": "", "renavam": "", "chassi": "", "marca_modelo": "", "ano_fabricacao": "", "ano_modelo": "", "cor": "", "proprietario": "", "cpf_cnpj": "", "endereco": "", "bairro": "", "cidade": "", "uf": "", "cep": "", "quantidade_eixos": "", "especie_tipo": "", "categoria":"", "categoria2":"" }. Ignore selos, assinaturas digitais e textos genéricos. Não use blocos de código. Responda somente com o JSON puro, sem formatação Markdown. sem quebra de linha.';


